import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import PanelTemplate from "../../templates/Panel";
import PageHeader from "../../components/PageHeader";
import Box from "../../components/Box";
import Button from "../../components/Button";
import InputDate from "../../components/InputDate";
import InputSelect from "../../components/InputSelect";

import api from "../../services/api";
import { renderError } from "../../helpers/errors";

import ReportResult from "./components/ReportResult";
import ModalExport from "./ModalExport";
import { CHART_TYPES, defaultPeriod, describeFilter } from "./helpers";

/**
 * Um relatório salvo, executado (BDM-1040).
 *
 * O período fica editável aqui de propósito: trocar o mês é a interação mais
 * comum e não deveria obrigar a abrir o construtor. As demais escolhas do
 * relatório aparecem só como leitura — quem muda estrutura é o construtor.
 */
export const ReportView = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [report, setReport] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chartType, setChartType] = useState('bar');
    const [dateField, setDateField] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dataset, setDataset] = useState(null);
    const [showExport, setShowExport] = useState(false);

    useEffect(() => {
        getReport();
    }, [id]);

    useEffect(() => {
        if (!report) return;

        if (!startDate || !endDate) {
            setLoading(false);
            return;
        }

        run();
    }, [report, dateField, startDate, endDate]);

    const getReport = () => {
        setLoading(true);

        api.get(`/report/${id}`)
            .then(res => {
                const resource = res.data.resource;
                const config = resource.config ?? {};

                // Os relatórios modelo nascem sem período de propósito, para não
                // ficarem presos ao mês em que a migration rodou; sem um padrão
                // aqui a tela abre sem janela e nunca chega a rodar.
                const period = defaultPeriod();

                setReport(resource);
                setChartType(config.chart?.type ?? 'bar');
                setDateField(config.date_field ?? null);
                setStartDate(config.start_date || period.start_date);
                setEndDate(config.end_date || period.end_date);

                return api.get(`/report/dataset`).then(datasets => {
                    setDataset(datasets.data.resources.find(item => item.key === resource.dataset) ?? null);
                });
            })
            .catch(error => {
                renderError(error);
                setLoading(false);
            });
    };

    const currentConfig = () => ({
        ...(report?.config ?? {}),
        date_field: dateField,
        start_date: startDate,
        end_date: endDate,
    });

    const run = () => {
        setLoading(true);

        api.post(`/report/run`, {
            dataset: report.dataset,
            report_id: report.id,
            config: currentConfig(),
        })
            .then(res => setResult(res.data.resource))
            .catch(renderError)
            .then(() => setLoading(false));
    };

    const duplicate = () => {
        api.post(`/report/${id}/duplicate`)
            .then(res => {
                toast.success('Cópia criada — ajuste o que precisar');
                navigate(`/report/${res.data.resource.id}/edit`);
            })
            .catch(renderError);
    };

    const grouped = (report?.config?.group_by ?? []).length > 0;

    const appliedFilters = (report?.config?.filters ?? [])
        .map((filter, index) => {
            const described = describeFilter(filter, dataset?.fields?.find(item => item.key === filter.field));

            return described ? { key: `${filter.field}-${index}`, ...described } : null;
        })
        .filter(Boolean);

    return (
        <PanelTemplate id="report-view">
            <PageHeader
                left={
                    <div>
                        <button type="button" className="report-back" onClick={() => navigate('/report')}>Relatórios</button>
                        <h1>{report?.name ?? 'Relatório'}</h1>
                        {report?.description && <span className="report-subtitle">{report.description}</span>}
                    </div>
                }
                right={
                    <>
                        <Button type={`secondary`} size={`small`} text={`Exportar`} action={() => setShowExport(true)} disabled={!result} />
                        <Button type={`secondary`} size={`small`} text={`Duplicar`} action={duplicate} />
                        {!report?.is_template &&
                            <Button type={`primary`} size={`small`} text={`Editar relatório`} action={() => navigate(`/report/${id}/edit`)} />
                        }
                    </>
                }
            />

            <Box>
                <div className="report-period">
                    {dataset && Object.keys(dataset.date_fields).length > 1 &&
                        <div className="filter-field">
                            <InputSelect
                                label={`Período por`}
                                options={Object.entries(dataset.date_fields).map(([value, label]) => ({ label, value }))}
                                value={dateField}
                                change={setDateField}
                            />
                        </div>
                    }
                    <div className="filter-field">
                        <InputDate label={`De`} format={`YYYY-MM-DD`} value={startDate} change={setStartDate} />
                    </div>
                    <div className="filter-field">
                        <InputDate label={`Até`} format={`YYYY-MM-DD`} value={endDate} change={setEndDate} />
                    </div>
                    {grouped &&
                        <div className="filter-field">
                            <InputSelect label={`Gráfico`} options={CHART_TYPES} value={chartType} change={setChartType} />
                        </div>
                    }
                </div>

                {appliedFilters.length > 0 &&
                    <div className="report-applied-filters">
                        {appliedFilters.map(filter => (
                            <span key={filter.key} className="report-filter-chip">
                                <span className="chip-label">{filter.label}</span>
                                {filter.value}
                            </span>
                        ))}
                    </div>
                }
            </Box>

            <Box title={`Resultado`} className={`no-padding`}>
                <ReportResult result={result} chartType={chartType} loading={loading} emptyMessage={`Escolha o período para ver o resultado.`} />
            </Box>

            <ModalExport
                show={showExport}
                close={() => setShowExport(false)}
                name={report?.name}
                dataset={report?.dataset}
                config={currentConfig()}
                result={result}
            />
        </PanelTemplate>
    );
};

export default ReportView;
